import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(t('invalidResetToken'));
      navigate('/login');
    }
  }, [token, navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetSuccess(true);
      toast.success(t('passwordResetSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('passwordResetError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="text-gray-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('backToLogin')}
        </Button>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white text-xl">{t('resetPassword')}</CardTitle>
            <CardDescription className="text-gray-400">
              {t('resetPasswordDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resetSuccess ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">{t('passwordChanged')}</h3>
                <p className="text-gray-400 text-sm mb-4">
                  {t('passwordChangedDescription')}
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-xl"
                >
                  {t('goToLogin')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-white flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t('newPassword')}
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                  />
                  <p className="text-xs text-gray-500">{t('minimum6Chars')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-white flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t('confirmPassword')}
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-xl"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resetting')}</>
                  ) : (
                    t('resetPassword')
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
